import React, {useState, useEffect, useMemo, useRef} from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    ChartOptions,
    ChartData,
    TooltipItem,
    Plugin
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Chart } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import moment from 'moment';
import {getAnnotationPeriods, getTimeSeriesData} from '../../services/api.service';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineController, LineElement, Title, Tooltip, Legend, TimeScale, zoomPlugin);

interface TimeSeriesDataPoint {
    timestamp: string;
    value: number | null;
}

interface TimeSeriesData {
    data: TimeSeriesDataPoint[];
    metadata: {
        station_id: string;
        variable: string;
        start_date: string;
        end_date: string;
        count: number;
        total_count: number;
    };
}

interface SensorAnnotationPeriod {
    start: string;
    end: string;
    text: string;
}

interface SolarRadiationChartProps {
    stationId: string;
    startDate: string;
    endDate: string;
    height?: number;
    showTahmoQCSolarRadiation?: boolean;
}

const SolarRadiationChart: React.FC<SolarRadiationChartProps> = ({
    stationId,
    startDate,
    endDate,
    height = 400,
    showTahmoQCSolarRadiation= false
}) => {
    // State management
    const [solarData, setSolarData] = useState<TimeSeriesData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [tahmoSolarRadiationPeriods, setTahmoSolarRadiationPeriods] = useState<SensorAnnotationPeriod[]>([]);
    const [tahmoSolarRadiationPeriodsCache, setTahmoSolarRadiationPeriodsCache] = useState<SensorAnnotationPeriod[]>([]);
     const [chartKey, setChartKey] = useState<number>(0); // Add key to force chart re-render

    const chartRef = useRef<any>(null);

     // Reset zoom function that properly accesses the chart instance
    const resetZoom = () => {
        if (chartRef.current) {
            // Access the chart instance through the react-chartjs-2 wrapper
            const chartInstance = chartRef.current;
            if (chartInstance && chartInstance.resetZoom) {
                chartInstance.resetZoom();
            } else if (chartInstance && chartInstance.chart && chartInstance.chart.resetZoom) {
                chartInstance.chart.resetZoom();
            }
        }
    };

    // Data fetching effect
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const solarResponse = await getTimeSeriesData(stationId, 'ra', startDate, endDate);
                setSolarData(solarResponse);
            } catch (err) {
                setError('Failed to fetch solar radiation data');
                console.error('Error fetching solar radiation data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [stationId, startDate, endDate]);

       useEffect(() => {
        const fetchSolarRadiationPeriods = async () => {
            try {
                const fetchHumidityResponse = await getAnnotationPeriods(stationId, 'solar_radiation_quality_tahmo_severe', startDate, endDate);
                const periods = fetchHumidityResponse.periods || [];

                // Always cache the fetched data
                setTahmoSolarRadiationPeriodsCache(periods);

                // Only display if toggle is enabled
                if (showTahmoQCSolarRadiation) {
                    setTahmoSolarRadiationPeriods(periods);
                } else {
                    setTahmoSolarRadiationPeriods([]);
                }
            } catch (err) {
                console.error('Failed to fetch solar radiation periods:', err);
                setTahmoSolarRadiationPeriodsCache([]);
                setTahmoSolarRadiationPeriods([]);
            }
        };

        fetchSolarRadiationPeriods();
    }, [stationId, startDate, endDate, showTahmoQCSolarRadiation]);

                    // Effect to handle toggle changes using cached data
    useEffect(() => {
        if (showTahmoQCSolarRadiation) {
            setTahmoSolarRadiationPeriods(tahmoSolarRadiationPeriodsCache);
        } else {
            setTahmoSolarRadiationPeriods([]);
        }
        setChartKey(prev => prev + 1);
    }, [showTahmoQCSolarRadiation, setTahmoSolarRadiationPeriodsCache]);

           // Create annotation plugin for battery issues
    const sensorAnnotationPlugin: Plugin<'line'> = useMemo(() => ({
        id: 'sensorAnnotation',
        beforeDraw: (chart: any) => {
            const ctx = chart.ctx;
            const chartArea = chart.chartArea;
            const xScale = chart.scales.x;

            // Helper function to draw text labels on periods
            const drawPeriodLabel = (startX: number, endX: number, text: string, color: string, yOffset: number = 0) => {
                const centerX = (startX + endX) / 2;
                const labelY = chartArea.top + 25 + yOffset;

                if (centerX >= chartArea.left && centerX <= chartArea.right) {
                    ctx.save();

                    // Measure text for background sizing
                    ctx.font = 'bold 12px Arial';
                    const textMetrics = ctx.measureText(text);
                    const textWidth = textMetrics.width;
                    const padding = 8;

                    // Draw background rectangle
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
                    ctx.fillRect(
                        centerX - textWidth / 2 - padding,
                        labelY - 15,
                        textWidth + padding * 2,
                        20
                    );

                    // Draw border
                    ctx.strokeStyle = color;
                    ctx.lineWidth = 2;
                    ctx.strokeRect(
                        centerX - textWidth / 2 - padding,
                        labelY - 15,
                        textWidth + padding * 2,
                        20
                    );

                    // Draw text
                    ctx.fillStyle = color;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(text, centerX, labelY - 5);

                    ctx.restore();
                }
            };

            // Helper function to draw dotted vertical lines
            const drawDottedLine = (x: number, color: string) => {
                if (x >= chartArea.left && x <= chartArea.right) {
                    ctx.save();
                    ctx.strokeStyle = color;
                    ctx.lineWidth = 2;
                    ctx.setLineDash([5, 5]);
                    ctx.beginPath();
                    ctx.moveTo(x, chartArea.top);
                    ctx.lineTo(x, chartArea.bottom);
                    ctx.stroke();
                    ctx.restore();
                }
            };


            // Draw stormy no rain(red)
            if (showTahmoQCSolarRadiation && tahmoSolarRadiationPeriods.length > 0) {
                ctx.save();
                ctx.fillStyle = 'rgba(220, 53, 69, 0.1)';

                tahmoSolarRadiationPeriods.forEach(period => {
                    const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                    if (startX <= chartArea.right && endX >= chartArea.left) {
                        ctx.fillRect(
                            Math.max(startX, chartArea.left),
                            chartArea.top,
                            Math.min(endX, chartArea.right) - Math.max(startX, chartArea.left),
                            chartArea.bottom - chartArea.top
                        );

                        // Draw period label
                        drawPeriodLabel(
                            Math.max(startX, chartArea.left),
                            Math.min(endX, chartArea.right),
                            'Severe solar radiation',
                            '#dc3545',
                            0
                        );

                    }
                });
                ctx.restore();

                // Draw dotted lines for period boundaries
                tahmoSolarRadiationPeriods.forEach(period => {
                    const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                    drawDottedLine(startX, 'rgba(220, 53, 69, 0.8)');
                    drawDottedLine(endX, 'rgba(220, 53, 69, 0.8)');
                });

            }
        }

    }), [showTahmoQCSolarRadiation, tahmoSolarRadiationPeriods]);

    // Helper function to get solar radiation intensity level
    const getSolarIntensityLevel = (value: number): string => {
        if (value <= 100) return 'Very Low';
        if (value <= 300) return 'Low';
        if (value <= 600) return 'Moderate';
        if (value <= 900) return 'High';
        return 'Very High';
    };

    // Helper function to get color based on solar intensity
    const getSolarIntensityColor = (value: number): string => {
        if (value <= 100) return '#6c757d'; // Gray
        if (value <= 300) return '#17a2b8'; // Light blue
        if (value <= 600) return '#ffc107'; // Yellow
        if (value <= 900) return '#fd7e14'; // Orange
        return '#dc3545'; // Red
    };

    // Create gradient background plugin
    const solarGradientPlugin: Plugin<'line'> = useMemo(() => ({
        id: 'solarGradient',
        beforeDraw: (chart) => {
            const ctx = chart.ctx;
            const chartArea = chart.chartArea;
            const yScale = chart.scales.y;

            // Define gradient zones
            const zones = [
                { min: 0, max: 100, color: 'rgba(108, 117, 125, 0.1)' },
                { min: 100, max: 300, color: 'rgba(23, 162, 184, 0.1)' },
                { min: 300, max: 600, color: 'rgba(255, 193, 7, 0.1)' },
                { min: 600, max: 900, color: 'rgba(253, 126, 20, 0.1)' },
                { min: 900, max: 1400, color: 'rgba(220, 53, 69, 0.1)' }
            ];

            ctx.save();
            zones.forEach(zone => {
                const yMin = yScale.getPixelForValue(zone.min);
                const yMax = yScale.getPixelForValue(zone.max);

                ctx.fillStyle = zone.color;
                ctx.fillRect(chartArea.left, yMax, chartArea.right - chartArea.left, yMin - yMax);
            });
            ctx.restore();
        }
    }), []);

    // Chart data configuration
    const chartData: ChartData<'line'> = useMemo(() => {
        if (!solarData?.data?.length) return { datasets: [] };

        const dataPoints = solarData.data
            .filter(point => point.value !== null)
            .map(point => ({
                x: moment(point.timestamp).valueOf(),
                y: point.value!
            }));

        return {
            datasets: [
                {
                    label: 'Solar Radiation (W/m²)',
                    data: dataPoints,
                    borderColor: '#ffc107',
                    backgroundColor: 'rgba(255, 193, 7, 0.1)',
                    borderWidth: 2,
                    pointRadius: 1,
                    pointHoverRadius: 4,
                    fill: true,
                    tension: 0.1,
                    pointBackgroundColor: (ctx) => {
                        // Add safety checks
                        if (!ctx.raw || typeof ctx.raw !== 'object') {
                            return '#ffc107'; // Default color
                        }

                        const value = ctx.raw as { y: number };
                        if (typeof value.y !== 'number') {
                            return '#ffc107'; // Default color
                        }

                        return getSolarIntensityColor(value.y);

                    },
                    pointBorderColor: (ctx) => {
                        // Add safety checks
                        if (!ctx.raw || typeof ctx.raw !== 'object') {
                            return '#ffc107'; // Default color
                        }

                        const value = ctx.raw as { y: number };
                        if (typeof value.y !== 'number') {
                            return '#ffc107'; // Default color
                        }

                        return getSolarIntensityColor(value.y);

                    }
                }
            ]
        };
    }, [solarData]);

    // Chart options configuration
    const chartOptions: ChartOptions<'line'> = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        },
        plugins: {
            title: {
                display: true,
                text: 'Solar Radiation Analysis',
                font: {
                    size: 16
                }
            },
            legend: {
                display: true,
                position: 'top'
            },
            tooltip: {
                callbacks: {
                    title: (context) => {
                        if (context.length > 0) {
                            return moment(context[0].parsed.x).format('DD MMM YYYY HH:mm');
                        }
                        return '';
                    },
                    label: (context) => {
                        const value = context.parsed.y;
                        const intensity = getSolarIntensityLevel(value);
                        return [
                            `Solar Radiation: ${value?.toFixed(1) || 0} W/m²`,
                            `Intensity: ${intensity}`
                        ];
                    },
                    afterBody: (tooltipItems) => {
                        const value = tooltipItems[0]?.parsed?.y;
                        if (value) {
                            const hour = moment(tooltipItems[0].parsed.x).hour();
                            let timeContext = '';

                            if (hour >= 6 && hour <= 18) {
                                timeContext = 'Daylight hours';
                            } else {
                                timeContext = 'Night hours';
                            }

                            return [`Time context: ${timeContext}`];
                        }
                        return [];
                    }
                }
            },
             // Zoom plugin configuration
            zoom: {
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true
                    },
                    mode: 'x',
                },
                pan: {
                    enabled: true,
                    mode: 'x',
                }
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    displayFormats: {
                        hour: 'HH:mm',
                        day: 'DD/MM',
                        week: 'DD/MM',
                        month: 'MM/YY'
                    },
                    tooltipFormat: 'DD MMM YYYY HH:mm'
                },
                title: {
                    display: true,
                    text: 'Date'
                }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Solar Radiation (W/m²)',
                    color: '#ffc107'
                },
                grid: {
                    drawOnChartArea: true,
                },
                beginAtZero: true,
                max: 1400,
                ticks: {
                     color: '#ffc107',
                    stepSize: 200,
                    callback: function (value: any) {
                        const intensity = getSolarIntensityLevel(value);
                        return `${value} W/m² (${intensity})`;
                    }
                }
            }
        },
        parsing: false,
        normalized: true,
        spanGaps: false
    }), []);

    // Loading state
    if (loading) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: `${height}px` }}>
                        <div className="text-muted">
                            <i className="fa fa-spinner fa-spin fa-3x mb-3"></i>
                            <p>Loading solar radiation data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-danger">{error}</div>
                </div>
            </div>
        );
    }

    // No data state
    if (!solarData?.data?.length) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-warning">No solar radiation data available</div>
                </div>
            </div>
        );
    }

    // Calculate summary statistics
    const validData = solarData.data.filter(point => point.value !== null);
    const values = validData.map(point => point.value!);

    const avgRadiation = values.length > 0 ? values.reduce((sum, val) => sum + val, 0) / values.length : 0;
    const maxRadiation = values.length > 0 ? Math.max(...values) : 0;
    const minRadiation = values.length > 0 ? Math.min(...values) : 0;

    // Calculate peak hours (radiation > 600 W/m²)
    const peakHours = validData.filter(point => point.value! > 600).length;
    const totalHours = validData.length;
    const peakPercentage = totalHours > 0 ? (peakHours / totalHours) * 100 : 0;

    // Calculate daily patterns
    const dayData = validData.filter(point => {
        const hour = moment(point.timestamp).hour();
        return hour >= 6 && hour <= 18;
    });
    const nightData = validData.filter(point => {
        const hour = moment(point.timestamp).hour();
        return hour < 6 || hour > 18;
    });

    const avgDayRadiation = dayData.length > 0 ? dayData.reduce((sum, d) => sum + d.value!, 0) / dayData.length : 0;
    const avgNightRadiation = nightData.length > 0 ? nightData.reduce((sum, d) => sum + d.value!, 0) / nightData.length : 0;

    // Main render
    return (
        <div className="card">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                    {/*<h5 className="card-title mb-0">{title}</h5>*/}
                    <div className="d-flex align-items-center gap-3">
                         {/* Reset zoom button */}
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={resetZoom}
                            title="Reset Zoom"
                        >
                            <i className="fa fa-search-minus me-1"></i>
                            Reset Zoom
                        </button>

                    </div>
                </div>
            </div>
            <div className="card-body">
                <div style={{ height: `${height}px` }}>
                    <Chart
                        key={chartKey}
                        ref={chartRef}
                        type="line" data={chartData} options={chartOptions} plugins={[solarGradientPlugin, sensorAnnotationPlugin]} />
                </div>

                {/* Intensity Legend */}
                <div className="mt-2 mb-3">
                    <div className="row">
                        <div className="col-12">
                            <small className="text-muted d-block mb-2">Solar Radiation Intensity Levels:</small>
                            <div className="d-flex flex-wrap gap-3">
                                <span className="badge" style={{ backgroundColor: '#6c757d', color: 'white' }}>
                                    0-100 W/m² Very Low
                                </span>
                                <span className="badge" style={{ backgroundColor: '#17a2b8', color: 'white' }}>
                                    100-300 W/m² Low
                                </span>
                                <span className="badge" style={{ backgroundColor: '#ffc107', color: 'black' }}>
                                    300-600 W/m² Moderate
                                </span>
                                <span className="badge" style={{ backgroundColor: '#fd7e14', color: 'white' }}>
                                    600-900 W/m² High
                                </span>
                                <span className="badge" style={{ backgroundColor: '#dc3545', color: 'white' }}>
                                    900+ W/m² Very High
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Summary Statistics */}
                <div className="mt-2">
                    <small className="text-muted">
                        Solar radiation analysis over time
                        <span className="ms-3">
                            Data points: {validData.length}
                        </span>
                        <span className="ms-3">
                            Average: {avgRadiation.toFixed(1)} W/m²
                        </span>
                        <span className="ms-3">
                            Peak: {maxRadiation.toFixed(1)} W/m²
                        </span>
                        <span className="ms-3">
                            Min: {minRadiation.toFixed(1)} W/m²
                        </span>
                        <span className="ms-3">
                            Peak hours: {peakPercentage.toFixed(1)}%
                        </span>
                        <span className="ms-3">
                            Day avg: {avgDayRadiation.toFixed(1)} W/m²
                        </span>
                        <span className="ms-3">
                            Night avg: {avgNightRadiation.toFixed(1)} W/m²
                        </span>
                    </small>
                </div>
            </div>
        </div>
    );
};

export default SolarRadiationChart;